import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import ContextMenu from '../components/ContextMenu';
import LivermorePriceChart from '../components/LivermorePriceChart';
import {
  addLivermoreTicker,
  deleteLivermoreTicker,
  getLivermoreRecords,
  getLivermoreTickers,
  updateLivermoreTicker,
  validateTicker,
  getCategories
} from '../services/api';
import { subscribeToTickerUpdates } from '../services/socketService';
import useQuickBuy from '../hooks/useQuickBuy';
import './Livermore.css';

const COLUMN_LABELS = {
  upward_trend: 'Uptrend',
  natural_rally: 'Ntrl Rally',
  secondary_rally: 'Sdry Rally',
  natural_reaction: 'Ntrl React',
  secondary_reaction: 'Sdry React',
  downward_trend: 'Downtrend'
};

const LIVERMORE_COLUMN_ORDER = [
  'secondary_rally',
  'natural_rally',
  'upward_trend',
  'downward_trend',
  'natural_reaction',
  'secondary_reaction'
];

const ALT_COLUMN_ORDER = [
  'upward_trend',
  'natural_rally',
  'secondary_rally',
  'natural_reaction',
  'secondary_reaction',
  'downward_trend'
];

const getDaysOnLivermore = (createdAt) => {
  if (!createdAt) return '--';
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return '--';
  const now = new Date();
  const diffMs = now.getTime() - createdDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

const formatPrice = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }
  return Number(value).toFixed(2);
};

const ET_TIMEZONE = 'America/New_York';

const getEtDateKey = (dateInput) => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(date); // YYYY-MM-DD
};

const formatShortDate = (dateString) => {
  if (!dateString) return '--';
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: ET_TIMEZONE });
  return `${mm}-${dd}-${yy} ${weekday}`;
};

const Livermore = () => {
  const [tickers, setTickers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [records, setRecords] = useState([]);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingTickers, setLoadingTickers] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [error, setError] = useState('');
  const [addSymbol, setAddSymbol] = useState('');
  const [addThreshold, setAddThreshold] = useState(0.03);
  const [addInitialColumn, setAddInitialColumn] = useState('upward_trend');
  const [addGroup, setAddGroup] = useState('');
  const [addGroupTouched, setAddGroupTouched] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    ticker: null
  });
  const [sortMode, setSortMode] = useState(() => {
    if (typeof window === 'undefined') return 'symbol';
    return localStorage.getItem('livermoreSortMode') || 'symbol';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    if (typeof window === 'undefined') return 'asc';
    return localStorage.getItem('livermoreSortDirection') || 'asc';
  });
  const [categories, setCategories] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [editGroup, setEditGroup] = useState('');
  const [editThreshold, setEditThreshold] = useState(0.03);
  const [chartMode, setChartMode] = useState(() => {
    if (typeof window === 'undefined') return 'price';
    return localStorage.getItem('livermoreChartMode') || 'price';
  });
  const [columnOrderMode, setColumnOrderMode] = useState(() => {
    if (typeof window === 'undefined') return 'livermore';
    return localStorage.getItem('livermoreColumnOrder') || 'livermore';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('livermoreSortMode', sortMode);
    localStorage.setItem('livermoreSortDirection', sortDirection);
  }, [sortMode, sortDirection]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('livermoreChartMode', chartMode);
  }, [chartMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('livermoreColumnOrder', columnOrderMode);
  }, [columnOrderMode]);
  const validateTimerRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const refreshTimerRef = useRef(null);

  const selectedTicker = useMemo(
    () => tickers.find((ticker) => ticker.livermore_ticker_id === selectedId),
    [tickers, selectedId]
  );

  const columnOrder = useMemo(
    () => (columnOrderMode === 'livermore' ? LIVERMORE_COLUMN_ORDER : ALT_COLUMN_ORDER),
    [columnOrderMode]
  );

  const columnDefs = useMemo(
    () => columnOrder.map((key) => ({ key, label: COLUMN_LABELS[key] || key })),
    [columnOrder]
  );

  const sortedTickers = useMemo(() => {
    const orderMap = new Map(columnOrder.map((key, index) => [key, index]));

    const list = [...tickers];
    if (sortMode === 'column') {
      list.sort((a, b) => {
        const aRank = orderMap.has(a.current_column) ? orderMap.get(a.current_column) : columnOrder.length;
        const bRank = orderMap.has(b.current_column) ? orderMap.get(b.current_column) : columnOrder.length;
        if (aRank !== bRank) {
          return sortDirection === 'asc' ? aRank - bRank : bRank - aRank;
        }
        return String(a.symbol || '').localeCompare(String(b.symbol || ''));
      });
      return list;
    }

    if (sortMode === 'group') {
      list.sort((a, b) => {
        const aGroup = String(a.group_name || '').toLowerCase();
        const bGroup = String(b.group_name || '').toLowerCase();
        const groupCompare = aGroup.localeCompare(bGroup);
        if (groupCompare !== 0) {
          return sortDirection === 'asc' ? groupCompare : -groupCompare;
        }
        const symbolCompare = String(a.symbol || '').localeCompare(String(b.symbol || ''));
        return sortDirection === 'asc' ? symbolCompare : -symbolCompare;
      });
      return list;
    }

    if (sortMode === 'days') {
      list.sort((a, b) => {
        const aDays = Number(getDaysOnLivermore(a.created_at));
        const bDays = Number(getDaysOnLivermore(b.created_at));
        const aValue = Number.isFinite(aDays) ? aDays : -1;
        const bValue = Number.isFinite(bDays) ? bDays : -1;
        if (aValue !== bValue) {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        const symbolCompare = String(a.symbol || '').localeCompare(String(b.symbol || ''));
        return sortDirection === 'asc' ? symbolCompare : -symbolCompare;
      });
      return list;
    }

    list.sort((a, b) => {
      const result = String(a.symbol || '').localeCompare(String(b.symbol || ''));
      return sortDirection === 'asc' ? result : -result;
    });
    return list;
  }, [tickers, sortMode, sortDirection, columnOrder]);

  const loadTickers = useCallback(async () => {
    try {
      setLoadingTickers(true);
      const response = await getLivermoreTickers();
      const nextTickers = response?.data?.tickers || [];
      setTickers(nextTickers);
      if (nextTickers.length > 0 && !nextTickers.find((item) => item.livermore_ticker_id === selectedId)) {
        setSelectedId(nextTickers[0].livermore_ticker_id);
      }
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load Livermore tickers.');
    } finally {
      setLoadingTickers(false);
    }
  }, [selectedId]);

  const loadRecords = useCallback(async (livermoreTickerId, nextPage = page, nextPageSize = pageSize) => {
    if (!livermoreTickerId) {
      setRecords([]);
      setPreview(null);
      return;
    }
    try {
      setLoadingRecords(true);
      const response = await getLivermoreRecords(livermoreTickerId, nextPage, nextPageSize);
      const nextRecords = response?.data?.records || [];
      const nextPreview = response?.data?.preview || null;
      setRecords(nextRecords);
      setPreview(nextPreview);
      setTotalPages(response?.data?.total_pages || 1);
      setPage(response?.data?.page || nextPage);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load Livermore records.');
    } finally {
      setLoadingRecords(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadTickers();
  }, [loadTickers]);

  useEffect(() => {
    if (!showAddModal && !editTarget) return;
    let isMounted = true;
    getCategories()
      .then((response) => {
        if (!isMounted) return;
        const items = response?.data || [];
        setCategories(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (isMounted) {
          setCategories([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [showAddModal, editTarget]);

  const ensureCategoriesLoaded = useCallback(() => {
    if (categories.length > 0) {
      return;
    }
    getCategories()
      .then((response) => {
        const items = response?.data || [];
        setCategories(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        setCategories([]);
      });
  }, [categories.length]);

  useEffect(() => {
    loadRecords(selectedId, page, pageSize);
  }, [selectedId, page, pageSize, loadRecords]);

  useEffect(() => {
    const unsubscribe = subscribeToTickerUpdates((payload) => {
      if (!payload?.updated_tickers || payload.updated_tickers.length === 0) {
        return;
      }
      if (!selectedTicker?.symbol) {
        return;
      }
      const wasUpdated = payload.updated_tickers.some(
        (ticker) => ticker?.symbol?.toUpperCase() === selectedTicker.symbol.toUpperCase()
      );
      if (!wasUpdated) {
        return;
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = setTimeout(() => {
        loadRecords(selectedId, page, pageSize);
        refreshTimerRef.current = null;
      }, 600);
    });

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      unsubscribe();
    };
  }, [selectedId, selectedTicker?.symbol, page, pageSize, loadRecords]);

  const handleAddTicker = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    const symbol = addSymbol.trim().toUpperCase();
    if (!symbol) {
      setError('Enter a ticker symbol.');
      return;
    }

    try {
      setIsAdding(true);
      try {
        const validation = await validateTicker(symbol);
        if (!validation?.data?.valid) {
          setError(validation?.data?.error || 'Ticker validation failed.');
          return;
        }
      } catch (validationError) {
        // Allow creation even if validation fails; ticker can be populated later
      }
      const response = await addLivermoreTicker({
        symbol,
        threshold_points: addThreshold,
        initial_column: addInitialColumn,
        group_name: addGroup
      });
      const newEntry = response?.data?.livermore_ticker;
      await loadTickers();
      if (newEntry?.livermore_ticker_id) {
        setSelectedId(newEntry.livermore_ticker_id);
      }
      setAddSymbol('');
      setAddThreshold(0.03);
      setAddInitialColumn('upward_trend');
      setAddGroup('');
      setAddGroupTouched(false);
      setShowAddModal(false);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add ticker.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditGroup = async () => {
    if (!editTarget) {
      return;
    }
    try {
      const response = await updateLivermoreTicker(editTarget.livermore_ticker_id, {
        group_name: editGroup,
        threshold_points: editThreshold
      });
      const updated = response?.data?.livermore_ticker;
      setTickers((prev) =>
        prev.map((item) =>
          item.livermore_ticker_id === editTarget.livermore_ticker_id ? { ...item, ...updated } : item
        )
      );
      setEditTarget(null);
      setEditGroup('');
      setEditThreshold(0.03);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update group.');
    }
  };

  const editGroupOptions = useMemo(() => {
    const names = new Set(categories.map((category) => category.name));
    const options = [...categories.map((category) => ({ value: category.name, label: category.name }))];
    if (editGroup && !names.has(editGroup)) {
      options.unshift({ value: editGroup, label: editGroup });
    }
    return options;
  }, [categories, editGroup]);

  const handleSymbolBlur = useCallback(async () => {
    const symbol = addSymbol.trim().toUpperCase();
    if (!symbol) {
      return;
    }
    if (addGroupTouched) {
      return;
    }
    try {
      const response = await validateTicker(symbol);
      const categoryName = response?.data?.ticker_info?.category_name;
      if (categoryName) {
        setAddGroup(categoryName);
      }
    } catch (err) {
      // ignore
    }
  }, [addSymbol, addGroupTouched]);

  const groupSuggestions = useMemo(() => {
    return categories.map((category) => category.name).filter(Boolean);
  }, [categories]);
  useEffect(() => {
    const symbol = addSymbol.trim().toUpperCase();
    if (!showAddModal || !symbol || addGroupTouched) {
      return;
    }
    if (validateTimerRef.current) {
      clearTimeout(validateTimerRef.current);
    }
    validateTimerRef.current = setTimeout(() => {
      handleSymbolBlur();
      validateTimerRef.current = null;
    }, 500);

    return () => {
      if (validateTimerRef.current) {
        clearTimeout(validateTimerRef.current);
      }
    };
  }, [addSymbol, addGroupTouched, showAddModal, handleSymbolBlur]);

  const handleRemoveTicker = async (livermoreTickerId) => {
    try {
      await deleteLivermoreTicker(livermoreTickerId);
      await loadTickers();
      if (livermoreTickerId === selectedId) {
        setSelectedId(null);
        setRecords([]);
        setPreview(null);
        setPage(1);
        setTotalPages(1);
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to remove ticker.');
    }
  };

  const handlePageChange = (nextPage) => {
    setPage((prev) => {
      const target = Math.max(1, Math.min(nextPage, totalPages));
      return target === prev ? prev : target;
    });
  };

  const handleContextMenu = (event, ticker) => {
    event.preventDefault();
    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      ticker
    });
  };

  const { openQuickBuy, quickBuyModals } = useQuickBuy({
    onOrderCreated: loadTickers,
    storageKey: 'livermore_quick_buy_default_fraction',
    loadAccounts: true
  });

  const today = new Date();
  const todayKey = getEtDateKey(today);
  const todayDay = todayKey ? new Date(`${todayKey}T00:00:00`).getDay() : today.getDay();

  const recordsByDate = useMemo(() => {
    const map = new Map();
    const lastRecordedDateKey = selectedTicker?.last_recorded_at
      ? getEtDateKey(selectedTicker.last_recorded_at)
      : null;
    const lastRecordedColumn = selectedTicker?.current_column;
    const lastRecordedPrice = selectedTicker?.last_recorded_price;
    records.forEach((record) => {
      const dateKey = record.recorded_at
        ? getEtDateKey(record.recorded_at)
        : 'Unknown';
      if (dateKey !== 'Unknown') {
        const day = new Date(`${dateKey}T00:00:00`).getDay();
        if (day === 0 || day === 6) {
          return;
        }
      }
      if (!map.has(dateKey)) {
        map.set(dateKey, {});
      }
      map.get(dateKey)[record.column] = record;
    });
    if (
      lastRecordedDateKey &&
      lastRecordedColumn &&
      lastRecordedPrice !== null &&
      lastRecordedPrice !== undefined
    ) {
      if (!map.has(lastRecordedDateKey)) {
        map.set(lastRecordedDateKey, {});
      }
      const existing = map.get(lastRecordedDateKey)[lastRecordedColumn];
      map.get(lastRecordedDateKey)[lastRecordedColumn] = existing
        ? { ...existing, price: lastRecordedPrice }
        : {
          livermore_record_id: 'latest',
          column: lastRecordedColumn,
          price: lastRecordedPrice,
          recorded_at: selectedTicker?.last_recorded_at || null,
          is_pivot: false,
          reason: 'Latest recorded close.'
        };
    }
    if (preview && preview.column) {
      const dateKey = getEtDateKey(new Date());
      const day = dateKey ? new Date(`${dateKey}T00:00:00`).getDay() : new Date().getDay();
      if (day !== 0 && day !== 6) {
        if (!map.has(dateKey)) {
          map.set(dateKey, {});
        }
        const existing = map.get(dateKey)?.[preview.column];
        if (!existing || !existing.recorded_at) {
          map.get(dateKey)[preview.column] = {
            livermore_record_id: 'preview',
            column: preview.column,
            price: preview.price ?? '--',
            recorded_at: null,
            is_pivot: false,
            reason: preview.reason
          };
        }
      }
    }
    const sortedDates = Array.from(map.keys()).sort();
    return {
      dates: sortedDates,
      map
    };
  }, [records, preview, selectedTicker?.last_recorded_at, selectedTicker?.last_recorded_price, selectedTicker?.current_column]);

  const columnClassMap = useMemo(() => ({
    upward_trend: 'livermore-up',
    downward_trend: 'livermore-down',
    natural_rally: 'livermore-neutral',
    secondary_rally: 'livermore-neutral',
    natural_reaction: 'livermore-neutral',
    secondary_reaction: 'livermore-neutral'
  }), []);

  const columnColorMap = useMemo(() => ({
    upward_trend: '#15803d',
    downward_trend: '#b91c1c',
    natural_rally: '#6b7280',
    secondary_rally: '#6b7280',
    natural_reaction: '#6b7280',
    secondary_reaction: '#6b7280'
  }), []);


  const chartPoints = useMemo(() => {
    return recordsByDate.dates.map((date) => {
      const recordByColumn = recordsByDate.map.get(date) || {};
      let record = null;
      let pivotRecord = null;
      for (const column of columnDefs) {
        const candidate = recordByColumn[column.key];
        if (!candidate) continue;
        if (candidate.is_pivot) {
          pivotRecord = candidate;
        }
        if (!record) {
          record = candidate;
        }
      }
      if (pivotRecord) {
        record = pivotRecord;
      }
      if (!record) {
        return null;
      }
      return {
        label: formatShortDate(date),
        price: Number(record.price),
        column: record.column,
        isPivot: Boolean(record.is_pivot)
      };
    }).filter(Boolean);
  }, [recordsByDate, columnDefs]);

  return (
    <div className="livermore-page container-xl">
      <div className="livermore-grid">
        <section className="livermore-panel livermore-panel-left">
          <div className="livermore-panel-header">
            <div className="livermore-panel-title">
              <div className="livermore-sort">
                <select
                  id="livermore-sort-mode"
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                >
                  <option value="symbol">Ticker</option>
                  <option value="group">Group</option>
                  <option value="column">Column</option>
                  <option value="days">Days</option>
                </select>
                <button
                  type="button"
                  className="livermore-sort-toggle"
                  onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </button>
                <span className="livermore-total">
                  Total: {sortedTickers.length} Pivot Breakout Only
                </span>
              </div>
              <button
                type="button"
                className="livermore-add-button"
                onClick={() => {
                  setError('');
                  setShowAddModal(true);
                }}
                aria-label="Add Livermore ticker"
              >
                +
              </button>
            </div>
          </div>

          {loadingTickers && <div className="livermore-status">Loading tickers...</div>}
          {!loadingTickers && tickers.length === 0 && (
            <div className="livermore-status">No Livermore tickers yet.</div>
          )}

          <div className="livermore-ticker-list">
            {sortedTickers.map((ticker) => (
              <div
                key={ticker.livermore_ticker_id}
                role="button"
                tabIndex={0}
                className={`livermore-ticker-item ${selectedId === ticker.livermore_ticker_id ? 'active' : ''}`}
                onClick={() => setSelectedId(ticker.livermore_ticker_id)}
                onContextMenu={(event) => handleContextMenu(event, ticker)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setSelectedId(ticker.livermore_ticker_id);
                  }
                }}
              >
                <span className="livermore-ticker-symbol">{ticker.symbol}</span>
                <button
                  type="button"
                  className="btn btn-success btn-sm livermore-quick-buy-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openQuickBuy({
                      symbol: ticker.symbol,
                      price: ticker.last_recorded_price ?? null
                    });
                  }}
                  title={`Quick buy ${ticker.symbol}`}
                >
                  +
                </button>
                <span className="livermore-ticker-group">{ticker.group_name || '--'}</span>
                <span className="livermore-ticker-price">
                  {ticker.last_recorded_price !== null && ticker.last_recorded_price !== undefined
                    ? `$${formatPrice(ticker.last_recorded_price)}`
                    : '--'}
                </span>
                <span className="livermore-ticker-days">
                  {getDaysOnLivermore(ticker.created_at)}
                </span>
                <span
                  className={`livermore-ticker-col ${
                    ticker.current_column ? `livermore-col-${ticker.current_column}` : ''
                  }`}
                >
                  {ticker.current_column ? COLUMN_LABELS[ticker.current_column] || ticker.current_column : '--'}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="livermore-panel livermore-panel-right">
          {loadingRecords && selectedTicker && <div className="livermore-status">Loading records...</div>}

          {!selectedTicker && (
            <div className="livermore-empty">
              Add a ticker on the left to start your Livermore record.
            </div>
          )}

          {selectedTicker && (
            <>
              {chartPoints.length > 0 && (
                <div className="livermore-chart-wrapper">
                  <div className="livermore-chart-controls">
                    <div className="livermore-chart-controls-left">
                      <span>Y Axis</span>
                      <div className="livermore-chart-toggle">
                        <button
                          type="button"
                          className={chartMode === 'price' ? 'active' : ''}
                          onClick={() => setChartMode('price')}
                        >
                          Price
                        </button>
                        <button
                          type="button"
                          className={chartMode === 'column' ? 'active' : ''}
                          onClick={() => setChartMode('column')}
                        >
                          Columns
                        </button>
                      </div>
                      <div className={`livermore-order-toggle ${chartMode === 'column' ? '' : 'is-hidden'}`}>
                        <span>Order</span>
                        <div className="livermore-chart-toggle">
                          <button
                            type="button"
                            className={columnOrderMode === 'livermore' ? 'active' : ''}
                            onClick={() => setColumnOrderMode('livermore')}
                          >
                            Livermore
                          </button>
                          <button
                            type="button"
                            className={columnOrderMode === 'alt' ? 'active' : ''}
                            onClick={() => setColumnOrderMode('alt')}
                          >
                            Alt
                          </button>
                        </div>
                      </div>
                    </div>
                    <div
                      className="livermore-chart-current-col"
                      style={{
                        color: columnColorMap[selectedTicker?.current_column] || 'var(--text-primary)'
                      }}
                    >
                      {selectedTicker?.current_column
                        ? COLUMN_LABELS[selectedTicker.current_column] || selectedTicker.current_column
                        : '--'}
                    </div>
                  </div>
                  <LivermorePriceChart
                    dataPoints={chartPoints}
                    columnLabels={COLUMN_LABELS}
                    columnColors={columnColorMap}
                    mode={chartMode}
                    columnOrder={columnOrder}
                    currentColumn={selectedTicker?.current_column || null}
                  />
                </div>
              )}
              <div className="livermore-table-wrapper">
                <table className="livermore-table">
                  <colgroup>
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '110px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date</th>
                      {columnDefs.map((column) => (
                        <th key={`head-${column.key}`}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recordsByDate.dates.map((date) => (
                      <tr key={`row-${date}`}>
                        <td className="livermore-date-cell">{formatShortDate(date)}</td>
                      {columnDefs.map((column) => {
                        const record = recordsByDate.map.get(date)?.[column.key];
                        if (!record) {
                          return <td key={`${column.key}-${date}`} className="livermore-empty-cell" />;
                          }
                          return (
                            <td key={`${column.key}-${date}`}>
                              <div
                                className={`livermore-record ${columnClassMap[column.key] || ''} ${record.is_pivot ? 'livermore-pivot' : ''}`}
                                title={record.reason || 'Livermore record'}
                              >
                                <span className="livermore-record-price">${formatPrice(record.price)}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {selectedTicker && (
            <div className="livermore-pagination livermore-pagination-left">
              <div className="livermore-page-size">
                <label htmlFor="livermore-page-size">Rows</label>
                <select
                  id="livermore-page-size"
                  value={pageSize}
                  onChange={(event) => {
                    const nextSize = Number(event.target.value);
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                >
                  {[10, 20, 40, 60].map((size) => (
                    <option key={`size-${size}`} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="livermore-page-controls">
                <button type="button" onClick={() => handlePageChange(1)} disabled={page <= 1}>
                  First
                </button>
                <button type="button" onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>
                  Prev
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button type="button" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
                  Next
                </button>
                <button type="button" onClick={() => handlePageChange(totalPages)} disabled={page >= totalPages}>
                  Last
                </button>
              </div>
            </div>
          )}

          {selectedTicker && todayDay >= 5 && (
            <div className="livermore-weekend-hint">
              Weekend data is skipped. Records will start on the next trading day.
            </div>
          )}
        </section>
      </div>

      {error && <div className="livermore-error">{error}</div>}

      {quickBuyModals}

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
      >
        <Modal.Header closeButton={!isAdding}>
          <Modal.Title>Add Livermore Ticker</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="livermore-add-form" onSubmit={handleAddTicker}>
            <Form.Group className="mb-3" controlId="livermore-add-symbol">
              <Form.Label>Ticker symbol</Form.Label>
              <Form.Control
                type="text"
                value={addSymbol}
                onChange={(event) => setAddSymbol(event.target.value.toUpperCase())}
                placeholder="e.g. AAPL"
                maxLength={10}
                disabled={isAdding}
                autoFocus
                onBlur={handleSymbolBlur}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="livermore-add-trend">
              <Form.Label>Initial column</Form.Label>
              <Form.Select
                value={addInitialColumn}
                onChange={(event) => setAddInitialColumn(event.target.value)}
                disabled={isAdding}
              >
                {columnDefs.map((column) => (
                  <option key={`initial-${column.key}`} value={column.key}>
                    {column.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="livermore-add-threshold">
              <Form.Label>Threshold (%)</Form.Label>
              <Form.Select
                value={addThreshold}
                onChange={(event) => setAddThreshold(Number(event.target.value))}
                disabled={isAdding}
              >
                <option value={0.03}>3%</option>
                <option value={0.05}>5%</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="livermore-add-group">
              <Form.Label>Group</Form.Label>
              <Form.Control
                list="livermore-group-suggestions"
                value={addGroup}
                onChange={(event) => {
                  setAddGroupTouched(true);
                  setAddGroup(event.target.value);
                }}
                placeholder="Type or select"
                disabled={isAdding}
              />
              <datalist id="livermore-group-suggestions">
                {groupSuggestions.map((name) => (
                  <option key={`group-${name}`} value={name} />
                ))}
              </datalist>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="livermore-add-form"
            disabled={isAdding}
          >
            {isAdding ? 'Adding...' : 'Add Ticker'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton={!isAdding}>
          <Modal.Title>Remove Livermore Ticker</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget ? (
            <div>
              Remove {deleteTarget.symbol}? This will delete its Livermore records.
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteTarget && handleRemoveTicker(deleteTarget.livermore_ticker_id)}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      <ContextMenu
        show={contextMenu.show}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        onHide={() => setContextMenu({ show: false, x: 0, y: 0, ticker: null })}
        items={[
          {
            label: 'Edit',
            onClick: () => {
              if (contextMenu.ticker) {
                setEditTarget(contextMenu.ticker);
                setEditGroup(contextMenu.ticker.group_name || '');
                setEditThreshold(contextMenu.ticker.threshold_points ?? 0.03);
                ensureCategoriesLoaded();
              }
            }
          },
          {
            label: 'Remove',
            danger: true,
            onClick: () => {
              if (contextMenu.ticker) {
                setDeleteTarget(contextMenu.ticker);
              }
            }
          }
        ]}
      />

      <Modal
        show={Boolean(editTarget)}
        onHide={() => setEditTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="livermore-edit-group">
              <Form.Label>Group</Form.Label>
              <Form.Control
                list="livermore-edit-group-suggestions"
                value={editGroup}
                onChange={(event) => setEditGroup(event.target.value)}
                placeholder="Type or select"
              />
              <datalist id="livermore-edit-group-suggestions">
                {editGroupOptions.map((option) => (
                  <option key={`edit-cat-${option.value}`} value={option.value} />
                ))}
              </datalist>
            </Form.Group>
            <Form.Group className="mb-3" controlId="livermore-edit-threshold">
              <Form.Label>Threshold (%)</Form.Label>
              <Form.Select
                value={editThreshold}
                onChange={(event) => setEditThreshold(Number(event.target.value))}
              >
                <option value={0.03}>3%</option>
                <option value={0.05}>5%</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditTarget(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditGroup}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Livermore;
